import { DataGridConfig } from 'components/DataGrid';
import OSInfo from 'components/OSInfo';
import history from 'history.js';
import { useAtom, useAtomValue } from 'jotai';
import callApi from 'lib/callApi';
import { truncateText } from 'lib/helper';
import { showFailureToast } from 'lib/ToastHelper';
import { useEffect, useMemo, useState } from 'react';

import RowActions from './RowActions';
import {
  assetDataQueryParamsAtom,
  orderbyAtom,
  paginationConfigAtom,
  selectedRowIdsAtom,
  sortOrderAtom,
  columnKeysAssetAtom,
  DEFAULT_COLUMN_KEYS_ASSET,
  assetDataAtom,
  forceRerenderAssetGridAtom,
  isQuerystringFiltersInitializedAtom,
  policyIdFilterAtom as assetsPolicyIdFilterAtom,
} from './state';
import ComplianceBar from '../Components/ComplianceBar';
import { getDrillDownUrl } from '../helper';

export const useAssetDataConfig = () => {
  const dataQueryParams = useAtomValue(assetDataQueryParamsAtom);
  const isQuerystringFiltersInitialized = useAtomValue(
    isQuerystringFiltersInitializedAtom,
  );
  const [listingData, setListingData] = useAtom(assetDataAtom);
  const [paginationConfig, setPaginationConfig] = useAtom(paginationConfigAtom);
  const [orderby, setOrderBy] = useAtom(orderbyAtom);
  const [sortOrder, setSortOrder] = useAtom(sortOrderAtom);
  const [selectedRowIds, setSelectedRowIds] = useAtom(selectedRowIdsAtom);

  const [columnKeysAsset, setColumnKeysAsset] = useAtom(columnKeysAssetAtom);

  const forceRenderGrid = useAtomValue(forceRerenderAssetGridAtom);
  const assetsPolicyId = useAtomValue(assetsPolicyIdFilterAtom);

  const [loading, setLoading] = useState(false);

  const enableSelectionAcrossPages = true;

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      if (dataQueryParams === null) return;
      try {
        setListingData({ ...listingData, loading: true });
        const responseData = await callApi(
          `/pcm/assets/`,
          'GET',
          dataQueryParams,
        );

        setListingData({ ...responseData?.data, loading: false });
      } catch (error) {
        const errorData = (error as any)?.data;
        const errorMessage = errorData
          ? `${Object.keys(errorData)[0]}: ${
              Object.values(errorData).flat()[0]
            }`
          : 'Failed to load data.';
        showFailureToast(errorMessage);
        setListingData({
          results: [],
          insights: {
            total: 0,
            total_failed_postures: 0,
            total_passed_postures: 0,
            unique_os_count: 0,
            unique_policy_count: 0,
            urgent: 0,
            critical: 0,
            serious: 0,
            medium: 0,
            minimal: 0,
          },
          count: 0,
          loading: false,
        });
      } finally {
        setLoading(false);
      }
    };

    if (isQuerystringFiltersInitialized) {
      fetchData();
    }
  }, [dataQueryParams, isQuerystringFiltersInitialized]);

  const config: DataGridConfig = useMemo(
    (): DataGridConfig => ({
      rowIdKey: 'host_id',
      columns: [
        {
          key: 'id',
          label: 'ID',
          sortable: true,
          headerTooltip: 'Unique Identifier for the Asset in SP360 Database',
          enableCopyToClipboard: true,
        },
        {
          key: 'host_id',
          label: 'Host ID',
          sortable: true,
          headerTooltip: 'Host Identifier',
          enableCopyToClipboard: true,
        },
        {
          key: 'name',
          label: 'Host',
          sortable: true,
          headerTooltip: 'Name of the Host Asset',
          enableCopyToClipboard: true,
          rowCellStyle: { fontWeight: 'bold', width: 'auto' },
          headerCellStyle: { width: 'auto' },
          // maxCharLength: 50,
          // width: '327px',
          overrideContents: (rowData) => {
            const name = rowData['name'];
            const displayName = name;

            return (
              <div>
                <strong>{truncateText(displayName, 45)}</strong>
              </div>
            );
          },
          tooltip: (rowData) => {
            const alias = rowData['alias'];
            const name = rowData['name'];

            return (
              <div>
                <div>
                  <strong>Host name:</strong> {truncateText(name, 27)}
                </div>
                {alias && (
                  <>
                    <strong>Alias:</strong> {truncateText(alias, 30)}
                  </>
                )}
              </div>
            );
          },
        },
        {
          key: 'name_with_alias',
          label: 'Host Info',
          sortable: true,
          headerTooltip:
            'Composite of Host Name, Type and Alias (primary if available)',
          enableCopyToClipboard: true,
          rowCellStyle: { fontWeight: 'bold', width: 'auto' },
          headerCellStyle: { width: 'auto' },
          // maxCharLength: 50,
          // width: '327px',
          overrideContents: (rowData) => {
            const alias = rowData['alias'];
            const name = rowData['name'];
            const displayName = alias || name;

            return (
              <div>
                <strong>{truncateText(displayName, 45)}</strong>
                {alias && <div className="small">{truncateText(name, 42)}</div>}
              </div>
            );
          },
          tooltip: (rowData) => {
            const alias = rowData['alias'];
            const name = rowData['name'];

            return (
              <div>
                <div>
                  <strong>Host name:</strong> {truncateText(name, 27)}
                </div>
                {alias && (
                  <>
                    <strong>Alias:</strong> {truncateText(alias, 30)}
                  </>
                )}
              </div>
            );
          },
        },
        {
          key: 'alias',
          label: 'Alias',
          sortable: true,
          headerTooltip: 'Custom alias of the endpoint',
        },
        {
          key: 'address',
          label: 'IP Address',
          sortable: true,
          headerTooltip: 'IP Address of the Asset',
          width: '85px',
        },
        {
          key: 'os',
          label: 'Operating System',
          overrideContents: (rowData) => (
            <OSInfo id={rowData['id']} os={rowData['os']} maxCharLength={40} />
          ),
          rowCellStyle: { textDecoration: 'none' },
          tooltip: (rowData) => rowData['os'],
          sortable: true,
          headerTooltip: 'Host operating system',
          width: '300px',
        },
        {
          key: 'last_compliance_scan',
          label: 'Last Scan',
          sortable: true,
          headerTooltip: 'Date of the Last Compliance Scan',
          rowCellClassName: 'text-nowrap',
          width: '135px',
          classRules: [
            {
              className: 'text-critical',
              ruleFunction: (rowData) => {
                const lastComplianceScan = new Date(
                  rowData.last_compliance_scan,
                );
                const currentDate = new Date();
                const timeDifference =
                  currentDate.getTime() - lastComplianceScan.getTime();
                const daysDifference = timeDifference / (1000 * 3600 * 24);
                return daysDifference > 20;
              },
            },
            {
              className: 'text-medium',
              ruleFunction: (rowData) => {
                const lastComplianceScan = new Date(
                  rowData.last_compliance_scan,
                );
                const currentDate = new Date();
                const timeDifference =
                  currentDate.getTime() - lastComplianceScan.getTime();
                const daysDifference = timeDifference / (1000 * 3600 * 24);
                return daysDifference > 10 && daysDifference <= 20;
              },
            },
          ],
          tooltip: (rowData) => {
            const lastComplianceScan = new Date(rowData.last_compliance_scan);
            const currentDate = new Date();
            const timeDifference =
              currentDate.getTime() - lastComplianceScan.getTime();
            const daysDifference = Math.ceil(
              timeDifference / (1000 * 3600 * 24),
            );
            return daysDifference > 0
              ? `Last scanned ${daysDifference} days ago`
              : 'Last scanned today';
          },
        },
        {
          key: 'failed_count',
          label: 'Failed',
          sortable: true,
          headerTooltip: (
            <span>
              Number of Failed Checks. <br />
              NOTE: Count is respective to current selected policy.
            </span>
          ),
          width: '60px',
          onClick: (rowData) => {
            if (!rowData.failed_count) return;
            history.push(
              getDrillDownUrl({
                policyId: assetsPolicyId,
                hosts_in: [rowData.host_id],
                status_in: ['Failed'],
              }),
            );
          },
        },
        {
          key: 'passed_count',
          label: 'Passed',
          sortable: true,
          headerTooltip: (
            <span>
              Number of Passed Checks. <br />
              NOTE: Count is respective to current selected policy.
            </span>
          ),
          width: '60px',
          onClick: (rowData) => {
            if (!rowData.passed_count) return;
            history.push(
              getDrillDownUrl({
                policyId: assetsPolicyId,
                hosts_in: [rowData.host_id],
                status_in: ['Passed'],
              }),
            );
          },
        },
        {
          key: 'compliance',
          label: 'Compliance',
          sortable: false,
          orderBy: 'failed_count',
          headerTooltip: 'Compliance Status of the Asset',
          width: '200px',
          overrideContents: (rowData) => (
            <ComplianceBar
              passedCount={rowData.passed_count}
              failedCount={rowData.failed_count}
              width="200px"
            />
          ),
        },
        {
          key: 'actions',
          label: 'Actions',
          headerTooltip: 'Available Actions',
          headerCellStyle: {
            textAlign: 'right',
            maxWidth: 'auto',
            minWidth: '150px !important',
          },
          rowCellStyle: {
            textAlign: 'right',
            minWidth: '150px !important',
            maxWidth: 'auto',
            whiteSpace: 'nowrap',
          },
          render: (rowData) => {
            return <RowActions rowData={rowData} />;
          },
        },
      ],
      columnsDisplayConfig: {
        enabledColumnKeys: columnKeysAsset,
        defaultColumnKeys: DEFAULT_COLUMN_KEYS_ASSET,
        keysToBeExcluded: [],
        onColumnsDisplayChange: (updatedColumnKeys) => {
          setColumnKeysAsset(updatedColumnKeys);
        },
      },
      paginationConfig: {
        total_count: listingData?.count || 0,
        pageNumber: paginationConfig.page - 1,
        pageSize: paginationConfig.page_size,
        onPageChange: (newPage: number) => {
          setPaginationConfig({
            page_size: paginationConfig.page_size,
            page: newPage + 1,
          });
          if (!enableSelectionAcrossPages) {
            setSelectedRowIds([]);
          }
        },
        onPageSizeChange: (newPageSize: number) => {
          setPaginationConfig({ page_size: newPageSize, page: 1 });
          setSelectedRowIds([]);
        },
      },
      sortingConfig: {
        orderBy: orderby,
        order: sortOrder,
        onOrderByChange: (value: string) => setOrderBy(value),
        onOrderChange: (value) => setSortOrder(value),
      },
      rowSelectionConfig: {
        enableSelectionAcrossPages: enableSelectionAcrossPages,
        preSelectedRows: selectedRowIds,
        onSelectionChange: (updatedRowIds) => {
          setSelectedRowIds(updatedRowIds);
        },
      },
      rowConfig: {
        classRules: [
          {
            className: 'applied-row',
            ruleFunction: (rowData) =>
              rowData['isAppliedForDashboard'] === true,
          },
          {
            className: 'warning-row',
            ruleFunction: (rowData) =>
              rowData.last_compliance_scan === null ||
              rowData.failed_count === null ||
              rowData.passed_count === null,
          },
        ],
      },
    }),
    [
      listingData,
      columnKeysAsset,
      orderby,
      sortOrder,
      paginationConfig,
      selectedRowIds,
      forceRenderGrid,
    ],
  );

  return { config, data: listingData?.results, loading };
};
